import type {
  Benchmark, Health, Milestone, Onboarding, Risk, RiskFlag, RiskSeverity,
} from '@/api/types';
import { ArcMeter } from '@/components/ArcMeter';
import { TrendChart } from '@/components/TrendChart';
import { SEQ_RAMP } from '@/components/viz';
import { formatDateTime, formatPercent } from '@/components/format';

/** The milestone keys, in funnel order, with the words a person reads. */
const MILESTONE_LABELS: Record<string, string> = {
  signup: 'Signed up',
  first_login: 'First login',
  first_record: 'First record created',
  first_invite: 'First colleague invited',
  first_share: 'First object shared',
  first_automation: 'First automation built',
};

const COMPONENT_LABELS: Record<string, string> = {
  activity: 'Activity',
  seat_utilization: 'Seat utilization',
  adoption_breadth: 'Adoption breadth',
  collaboration: 'Collaboration',
  configuration: 'Configuration investment',
};

/** Maps onto the callout classes that already exist — there is no `callout-danger`. */
const SEVERITY_CALLOUT: Record<RiskSeverity, string> = {
  critical: 'callout-error',
  warning: 'callout-warning',
  info: 'callout-info',
};

function label(map: Record<string, string>, key: string): string {
  return map[key] ?? key.replace(/_/g, ' ');
}

/**
 * The health score, and never the number on its own.
 *
 * <b>The breakdown is not a debug affordance — it is the feature.</b> An account manager asked to
 * act on "41" will ask why within a minute, and a score they cannot answer that question about gets
 * ignored inside a month. So the components ship visible, with the exclusions beside them.
 */
export function HealthPanel({ health }: { health: Health }) {
  // Nothing measurable is UNSCORED. Rendering 0 here would show a healthy account as failing on the
  // one page whose entire purpose is deciding who to phone — the worst error this panel can make.
  if (health.unscored) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Health</h2>
          <span className="t-label subtle">{health.score_version}</span>
        </div>

        <div className="callout callout-warning">
          <strong>Not scored.</strong>{' '}
          Nothing this policy measures could be read for this account, so there is no score — which
          is not the same as a bad one.
        </div>

        <ExcludedList excluded={health.excluded} />
      </div>
    );
  }

  // Date-only. TrendChart's axis formatter builds `${iso}T00:00:00Z`, so a full timestamp produces
  // an invalid Date and it falls back to printing the raw string — which then overflows the plot at
  // both edges. Caught by looking at the screenshot; nothing about the types would have said so.
  const trend = health.history.map((p) => ({ date: p.computed_at.slice(0, 10), value: p.total }));

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Health</h2>
        <span className="t-label subtle">
          {health.score_version} · computed {formatDateTime(health.computed_at)}
        </span>
      </div>

      <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'minmax(200px, 260px) 1fr' }}>
        <div>
          <ArcMeter
            value={health.total / 100}
            display={`${Math.round(health.total)}`}
            label="out of 100"
            tone={health.total >= 70 ? 'good' : health.total >= 40 ? 'warning' : 'critical'}
          />

          {/* Coverage rides with the total, always. A 90 over two of five components and a 90 over
              all five are different claims, and only one of them is worth quoting. */}
          <p className="t-caption subtle" style={{ margin: '8px 0 0', textAlign: 'center' }}>
            {formatPercent(health.coverage)} of the policy could be measured
          </p>
        </div>

        <div>
          <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>Where it comes from</h3>

          <table className="table t-body" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Component</th>
                <th style={{ textAlign: 'right' }}>Weight</th>
                <th style={{ textAlign: 'right' }}>Score</th>
                <th style={{ textAlign: 'right' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {health.components.map((c) => (
                <tr key={c.name}>
                  <td>
                    {label(COMPONENT_LABELS, c.name)}
                    <div className="t-caption subtle">{c.explanation}</div>
                  </td>
                  <td style={{ textAlign: 'right' }} className="mono">
                    {/* Both weights, when they differ: what the policy intended, and what it
                        actually counted for after the rescale. Showing only one hides the rescale. */}
                    {Math.round(c.effective_weight)}
                    {Math.round(c.effective_weight) !== Math.round(c.weight) && (
                      <span className="subtle"> (of {Math.round(c.weight)})</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }} className="mono">{formatPercent(c.value)}</td>
                  <td style={{ textAlign: 'right' }} className="mono">{c.points.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="t-label muted">Total</td>
                <td style={{ textAlign: 'right' }} className="mono">{health.total.toFixed(1)}</td>
              </tr>
            </tfoot>
          </table>

          <ExcludedList excluded={health.excluded} />
        </div>
      </div>

      {health.history.length > 1 && (
        <div style={{ marginTop: 24 }}>
          <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>Trend</h3>
          {/* "41" says little. "68 → 54 → 41 over three weeks" says go and phone them. */}
          <TrendChart
            series={[{ key: 'health', label: 'Health', color: SEQ_RAMP[2], points: trend }]}
            height={120}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The components the policy wanted and could not have.
 *
 * Named rather than silently dropped, because the reason usually points at the collector rather
 * than at the customer — "we could not read their workflow database" is a ticket for us, and it
 * reads completely differently from "they have not built any workflows".
 */
function ExcludedList({ excluded }: { excluded: Health['excluded'] }) {
  if (excluded.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>
        Not measured ({excluded.length})
      </h3>
      <ul className="t-caption subtle" style={{ margin: 0, paddingLeft: 18 }}>
        {excluded.map((e) => (
          <li key={e.name}>
            <strong>{label(COMPONENT_LABELS, e.name)}</strong> ({Math.round(e.weight)} points) —{' '}
            {e.reason}
          </li>
        ))}
      </ul>
      <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
        Excluded components are not scored zero. The remaining weights were rescaled to carry the
        whole score.
      </p>
    </div>
  );
}

/**
 * Churn risk: a list of reasons, never a percentage.
 *
 * "68% likely to churn" would be a number with a decimal point and no calibration behind it, and
 * people act on decimal points. Each flag here carries the measurement that raised it, so it can be
 * argued with — "the owner has not signed in for 34 days" is checkable in a way "at risk" is not.
 */
export function RiskPanel({ risk }: { risk: Risk }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Risk</h2>
        <span className="t-label subtle">
          {risk.risk_level === 0
            ? 'nothing raised'
            : `${risk.risk_level} ${risk.risk_level === 1 ? 'flag' : 'flags'}`}
        </span>
      </div>

      {risk.flags.length === 0 ? (
        <div className="empty-state t-body">
          No risk flags raised.
          <div className="t-label subtle" style={{ marginTop: 4 }}>
            Nothing this policy checks for is currently true of this account.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {risk.flags.map((f) => <FlagCard key={f.id} flag={f} />)}
        </div>
      )}
    </div>
  );
}

export function FlagCard({ flag }: { flag: RiskFlag }) {
  return (
    <div className={`callout ${SEVERITY_CALLOUT[flag.severity] ?? 'callout-info'}`}>
      {/* One child, because `.callout` is a flex ROW — three siblings here laid the title, the
          evidence and the action out side by side and squeezed each into a column. */}
      <div>
        <strong>{flag.title}</strong>
        <div className="t-caption" style={{ marginTop: 4 }}>{flag.evidence}</div>
        {/* The action is not decoration. A finding that does not say what to do is noise, and once
            the list is noisy the one that mattered gets ignored with the rest. */}
        <div className="t-caption subtle" style={{ marginTop: 4 }}>{flag.action}</div>
      </div>
    </div>
  );
}

/**
 * The onboarding funnel — cumulative, by construction.
 *
 * Reaching a later milestone means every earlier one is reached too, whether or not its own
 * evidence survived. A non-cumulative funnel draws a drop-off that never happened, because an
 * account that raced ahead renders identically to one that skipped a step.
 */
export function OnboardingPanel({ onboarding }: { onboarding: Onboarding }) {
  const steps = [...onboarding.steps].sort((a, b) => a.ordinal - b.ordinal);
  const reached = steps.filter((s) => s.reached).length;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Onboarding</h2>
        <span className="t-label subtle">{reached} of {steps.length} reached</span>
      </div>

      {steps.length === 0 ? (
        <div className="empty-state t-body">Onboarding has not been derived for this account yet.</div>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {steps.map((s) => <MilestoneRow key={s.milestone} step={s} />)}
        </ol>
      )}

      <p className="t-caption subtle" style={{ margin: '12px 0 0' }}>
        {onboarding.time_to_value_days === null
          ? // Both ends are needed. Sharing is often known to have happened with no recoverable
            // date, and a duration against a missing end would be a made-up number in a real label.
            'Time to value is not computable — the first share has no recorded date.'
          : `Time to value: ${Math.round(onboarding.time_to_value_days)} days from signup to the first shared object.`}
      </p>
    </div>
  );
}

function MilestoneRow({ step }: { step: Milestone }) {
  return (
    <li style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <span
        aria-hidden
        style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: step.reached ? 'var(--success)' : 'var(--border)',
        }}
      />
      <span className="t-body" style={{ color: step.reached ? 'var(--text-main)' : 'var(--text-subtle)' }}>
        {label(MILESTONE_LABELS, step.milestone)}
      </span>
      <span className="t-caption subtle" style={{ marginLeft: 'auto' }}>
        {/* Reached with no date is a real state, and says so. Inventing a date would put a
            fabricated timestamp beside measured ones in the same list. */}
        {step.reached
          ? step.reached_at
            ? formatDateTime(step.reached_at)
            : 'reached · date unknown'
          : 'not yet'}
      </span>
    </li>
  );
}

/**
 * Platform percentiles, with the denominator that makes them readable.
 *
 * <b>Below the floor the percentiles are hidden with their reason, not shown.</b> A median over four
 * accounts renders exactly as confidently as one over four hundred and moves wildly with the fifth,
 * and a confident-looking wrong number gets quoted in a QBR deck.
 */
export function BenchmarksPanel({
  benchmarks, values,
}: {
  benchmarks: Benchmark[];
  /** This account's own value per metric, where it has one — for the "you are here" marker. */
  values?: Record<string, number | null>;
}) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Benchmarks</h2>
        <span className="t-label subtle">customers only, internal environments excluded</span>
      </div>

      {benchmarks.length === 0 ? (
        <div className="empty-state t-body">No benchmarks have been computed yet.</div>
      ) : (
        <table className="table t-body" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Metric</th>
              <th style={{ textAlign: 'right' }}>p25</th>
              <th style={{ textAlign: 'right' }}>Median</th>
              <th style={{ textAlign: 'right' }}>p75</th>
              <th style={{ textAlign: 'right' }}>p90</th>
              <th style={{ textAlign: 'right' }}>n</th>
              {values && <th style={{ textAlign: 'right' }}>This account</th>}
            </tr>
          </thead>
          <tbody>
            {benchmarks.map((b) => (
              <tr key={b.metric}>
                <td>{label(COMPONENT_LABELS, b.metric)}</td>

                {b.published ? (
                  <>
                    <td style={{ textAlign: 'right' }} className="mono">{benchmarkValue(b.metric, b.p25)}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{benchmarkValue(b.metric, b.median)}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{benchmarkValue(b.metric, b.p75)}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{benchmarkValue(b.metric, b.p90)}</td>
                  </>
                ) : (
                  // One cell, with the reason. Four em dashes would read as "we measured nothing";
                  // this reads as "we are not willing to publish it yet", which is what is true.
                  <td colSpan={4} className="t-caption subtle" style={{ textAlign: 'center' }}>
                    hidden — needs {b.minimum_sample} accounts, has {b.sample_size}
                  </td>
                )}

                <td style={{ textAlign: 'right' }} className="mono">{b.sample_size}</td>

                {values && (
                  <td style={{ textAlign: 'right' }} className="mono">
                    {formatPercent(values[b.metric] ?? null)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="t-caption subtle" style={{ margin: '12px 0 0' }}>
        Percentiles are withheld below {benchmarks[0]?.minimum_sample ?? 10} measured accounts.
        Tenants marked internal are excluded from every figure here.
      </p>
    </div>
  );
}

/** A benchmark cell: `health` is a 0–100 total; every other metric is a 0–1 rate. */
export function benchmarkValue(metric: string, value: number | null | undefined): string {
  if (metric === 'health') {
    return value === null || value === undefined || !Number.isFinite(value) ? '—' : Math.round(value).toString();
  }
  return formatPercent(value);
}
